import type { z } from "zod";

/**
 * Câu báo khi ô tiền nhận một số DÀI QUÁ (lỗi 23/09/2026, anh chốt câu
 * "<tên ô> quá lớn — kiểm tra lại số" cùng ngày).
 */
export function tooBigMessage(label: string): string {
  return `${label} quá lớn — kiểm tra lại số`;
}

/**
 * Tham số cho `.int()` của một ô tiền: `z.number().int(tooBigInt("Tiền cọc"))`.
 *
 * `.int()` của zod v4 ngoài "phải là số nguyên" còn chặn số vượt khoảng an toàn
 * của trình duyệt (từ 9.007.199.254.740.992 — gõ nhầm một dãy 16–17 chữ số là
 * dính). Không có câu riêng thì form tự parse rồi in `issues[0].message` hiện
 * nguyên văn tiếng Anh "Too big: expected int to be <=9007199254740991".
 *
 * Lỗi khác của `.int()` (số lẻ) trả `undefined` để zod dùng câu tầng ngoài —
 * mọi ô dùng hàm này chỉ nhận chữ số nên không gặp. `label` là hàm khi tên ô
 * nằm trong đường dẫn: ô từng tháng → "Mục tiêu tháng 2026-03".
 */
export function tooBigInt(label: string | ((path: PropertyKey[]) => string)): {
  error: z.core.$ZodErrorMap;
} {
  return {
    error: (issue) => {
      if (issue.code !== "too_big") return undefined;
      return tooBigMessage(typeof label === "string" ? label : label(issue.path ?? []));
    },
  };
}

/** Câu cho số lẻ ở ô số nguyên (anh chốt 24/09/2026). */
export function notWholeMessage(label: string): string {
  return `${label} phải là số nguyên`;
}

/** Câu cho số âm ở ô không nhận số âm (anh chốt 24/09/2026). */
export function negativeMessage(label: string): string {
  return `${label} không được âm`;
}

/**
 * Tham số cho `.int()` của ô số nguyên người dùng gõ TỰ DO — ô type=number nhận
 * cả dấu trừ lẫn dấu phẩy, nên `.int()` ném đủ ba lỗi, mỗi lỗi một câu:
 *   - số lẻ (6,5)             → "<tên ô> phải là số nguyên"
 *   - số quá dài              → `tooBig`   (mặc định "<tên ô> quá lớn — kiểm tra lại số")
 *   - số âm quá dài           → `tooSmall` (mặc định "<tên ô> không được âm")
 *
 * `tooSmall` nên trùng câu của `.nonnegative()` / `.min()` đi sau: số âm ngắn
 * (−5) lọt qua `.int()` và rơi vào câu đó, số âm dài báo ở đây — hai đường
 * phải nói cùng một câu. Ô có trần sẵn (Số buổi ≤ 30) truyền câu trần làm
 * `tooBig` để số quá dài nói đúng giới hạn thật.
 */
export function wholeInt(
  label: string,
  msgs: { tooBig?: string; tooSmall?: string } = {},
): { error: z.core.$ZodErrorMap } {
  return {
    error: (issue) => {
      if (issue.code === "too_big") return msgs.tooBig ?? tooBigMessage(label);
      if (issue.code === "too_small") return msgs.tooSmall ?? negativeMessage(label);
      return notWholeMessage(label);
    },
  };
}
